import {autocompletion} from '@codemirror/autocomplete';
import {EditorSelection} from "@codemirror/state";
import {isSingleCursor, selectedTypes, selectedTypesNoneOf} from '../state.js';

import {userMention} from "./userMention.js";

export default function (
  usernamesSource,
  noLanguageLabel,
  codeBlockLanguages,
  emojis,
  emojiImageUrl,
  emoticonsSectionName,
  usersSectionName,
  languageSectionName,
  shortEmoticonSectionName,
  shortEmoticons,
) {
  return autocompletion({
    activateOnTyping: true,
    activateOnTypingDelay: 0,
    interactionDelay: 0,
    defaultKeymap: false,
    closeOnBlur: false,
    icons: false,
    addToOptions: [{
      render: optionNode(emojiImageUrl),
      position: 20,
    }],
    optionClass,
    compareCompletions() {
      return 0;
    },
    override: [
      emojiSource(emoticonsSectionName, emojis),
      shortEmojisSource(shortEmoticonSectionName, shortEmoticons),
      userMentionSource(usersSectionName, usernamesSource),
      codeFragmentSource(languageSectionName, noLanguageLabel, codeBlockLanguages),
    ],
  });
}

function shortEmojisSource(sectionName, shortEmoticons) {
  return context => {
    if (!context.state.selection.main.empty) {
      return;
    }
    const pos = context.state.selection.main.anchor;
    const node = context.state.tree.topNode.resolve(pos, -1);
    if (node.name === 'Document' || node.name === 'Paragraph') {
      const atBeginning = context.matchBefore(/^[:<;^-][^:]*/);
      if (atBeginning) {
        return {
          from: atBeginning.from,
          options: shortEmojiOptions(shortEmoticons, sectionName),
        }
      }
      const afterColon = context.matchBefore(/[ !?:,][:<;^-][^:]*/);
      if (afterColon) {
        return {
          from: afterColon.from + 1,
          options: shortEmojiOptions(shortEmoticons, sectionName),
        }
      }
    }
  };
}

function shortEmojiOptions(shortEmoticons, sectionName) {
  return Object.entries(shortEmoticons)
    .flatMap(([shortCode, emoticons]) => {
      return emoticons.map(emoticon => ({
        section: {name: sectionName, rank: 1},
        autocompletion: 'short-emoji',
        label: shortCode,
        detail: emoticon,
        apply: emoticon + ' ',
      }));
    });
}

function emojiSource(sectionName, emojis) {
  return emojiCompletions(Object.entries(emojis)
    .map(([label, detail]) => ({
      label,
      section: {name: sectionName, rank: 2},
      detail: optionTitle(detail, label),
      autocompletion: 'emoji',
    })));
}

function optionTitle(detail, label) {
  const name = label.substring(1, label.length - 1);
  if (similar(name, detail)) {
    return null;
  }
  return detail;
}

function similar(primary, secondary) {
  return primary.toLowerCase().replaceAll(/[-_]/g, '')
    === secondary.toLowerCase().replaceAll(' ', '');
}

function emojiCompletions(options) {
  return context => {
    const pos = context.state.selection.main.anchor;
    const node = context.state.tree.topNode.resolve(pos, -1);
    if (node.name === 'Emoji') {
      return {
        from: node.from,
        options: options.map(option => ({
          ...option,
          apply(view, option) {
            view.dispatch({
              changes: {
                from: node.from,
                to: node.to,
                insert: option.label,
              },
              selection: EditorSelection.single(node.from + option.label.length),
            });
          },
        })),
      };
    }
    if (node.name === 'Document' || node.name === 'Paragraph') {
      const afterLetter = context.matchBefore(/[ !?:,]:\w*/);
      if (afterLetter) {
        return {from: afterLetter.from + 1, options};
      }
      const atBeginning = context.matchBefore(/^:\w*/);
      if (atBeginning) {
        return {from: atBeginning.from, options};
      }
    }
  };
}

function userMentionSource(sectionName, usernamesSource) {
  const mentionRegex = /(@{?)((?! )[0-9a-zA-ZąćęłńóśźżĄĆĘŁŃÓŚŹŻ#_@.\- ]+)$/;
  return context => {
    if (canShowUserAutocomplete(context)) {
      return usernameCompletions(sectionName, context, mentionRegex, usernamesSource);
    }
  };
}

function canShowUserAutocomplete({state}) {
  if (isSingleCursor(state)) {
    return selectedTypesNoneOf(state, ['FencedCode', 'CodeBlock', 'InlineCode']);
  }
  return false;
}

function usernameCompletions(sectionName, context, mentionRegex, usernamesSource) {
  const mentionPrefix = context.matchBefore(mentionRegex);
  if (mentionPrefix === null) {
    return;
  }
  if (/\w/.test(context.state.sliceDoc(mentionPrefix.from - 1, mentionPrefix.from))) { // this is untested
    return;
  }
  const {username, markOffset, mentionPrefixOffset} = lastUserMention(mentionRegex, mentionPrefix);
  return usernamesSource(username).then(users => usernameCompletion(sectionName, mentionPrefixOffset, markOffset, users));
}

function lastUserMention(mentionRegex, mentionPrefix) {
  const [_, mark, username] = mentionRegex.exec(mentionPrefix.text);
  const offset = lastUserMentionOffset(username, mark);
  return {
    username: username.slice(offset),
    mentionPrefixOffset: mentionPrefix.from + offset,
    markOffset: mark.length,
  };
}

function lastUserMentionOffset(username, mark) {
  const lastMention = ' @';
  const nestedMentionIndex = username.lastIndexOf(lastMention);
  if (nestedMentionIndex > -1 && mark === '@') {
    return nestedMentionIndex + lastMention.length;
  }
  return 0;
}

function usernameCompletion(sectionName, from, markOffset, users) {
  return {
    from: from + markOffset,
    options: users.map(({name, badge, avatar}) => {
      return usernameCompletionOption(sectionName, markOffset, name, badge, avatar);
    }),
  };
}

function usernameCompletionOption(sectionName, markOffset, name, badge, avatar) {
  return {
    section: sectionName,
    autocompletion: 'user',
    label: name,
    detail: badge,
    avatar,
    apply(view, option, from, to) {
      view.dispatch({changes: {from: from - markOffset, to, insert: userMention(name) + ' '}});
    },
  };
}

function codeFragmentSource(sectionName, noLanguageLabel, codeBlockLanguages) {
  return ({state}) => {
    if (!isSingleCursor(state)) {
      return null;
    }
    if (selectedTypes(state).has('InlineCode')) {
      return null;
    }
    const markOrInfo = state.tree.topNode.resolve(state.selection.main.from, -1);
    if (markOrInfo.type.name === 'CodeInfo') {
      return languageNameCompletion(sectionName, markOrInfo.from, noLanguageLabel, codeBlockLanguages);
    }
    if (markOrInfo.type.name === 'CodeMark') {
      if (markOrInfo.from === markOrInfo.parent.from) {
        return languageNameCompletion(sectionName, markOrInfo.to, noLanguageLabel, codeBlockLanguages);
      }
    }
  };
}

function languageNameCompletion(sectionName, from, noLanguageLabel, codeBlockLanguages) {
  const noLanguage = {autocompletion: 'code', detail: noLanguageLabel, label: ''};
  return {
    from,
    options: [
      noLanguage,
      ...Object.entries(codeBlockLanguages).map(([tag, name]) => {
        return {
          section: sectionName,
          autocompletion: 'code',
          label: tag,
          detail: tag.toLowerCase() === name.toLowerCase() ? null : name,
        }
      }),
    ],
  };
}

function optionClass({autocompletion}) {
  if (autocompletion === 'user') {
    return 'cm-completionOption cm-completionUser';
  }
  return 'cm-completionOption cm-completionCode';
}

function optionNode(emojiImageUrl) {
  return ({autocompletion, label, detail, avatar}) => {
    if (autocompletion === 'user') {
      return imageNode(avatar);
    }
    if (autocompletion === 'emoji') {
      return imageNode(emojiImageUrl(label.substring(1, label.length - 1)));
    }
    if (autocompletion === 'short-emoji') {
      return imageNode(emojiImageUrl(detail.substring(1, detail.length - 1)));
    }
    if (label === '') {
      return emptyNode();
    }
    return capitalNode(label[0]);
  };
}

function emptyNode() {
  return document.createElement('span');
}

function capitalNode(letter) {
  const span = document.createElement('span');
  span.classList.add('cm-autocompletion-capital');
  span.innerText = letter;
  return span;
}

function imageNode(avatar) {
  if (typeof avatar === "string") {
    const icon = document.createElement('img');
    icon.src = avatar;
    return icon;
  }
  return avatar;
}
